import React, { useState } from 'react';
import { motion } from 'motion/react';
import { ArrowRight, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface OrganizationData {
  name: string;
  description: string;
  industry: string;
  size: string;
  location: string;
  energyGoals: string;
  teamSize: string;
  contactEmail: string;
  contactPhone: string;
}

interface CreateOrganizationStepThreeFormProps {
  organizationData: OrganizationData;
  onComplete: () => void;
  isCreating?: boolean;
}

const CreateOrganizationStepThreeForm: React.FC<CreateOrganizationStepThreeFormProps> = ({
  organizationData,
  onComplete,
  isCreating = false,
}) => {
  const navigate = useNavigate();
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (agreedToTerms && !isCreating) {
      onComplete();
    }
  };

  const getIndustryLabel = (value: string) => {
    const options = {
      'renewable-energy': 'Ανανεώσιμες Πηγές Ενέργειας',
      solar: 'Ηλιακή Ενέργεια',
      wind: 'Αιολική Ενέργεια',
      hydro: 'Υδροηλεκτρική Ενέργεια',
      'energy-storage': 'Αποθήκευση Ενέργειας',
      'smart-grid': 'Έξυπνα Δίκτυα',
      consulting: 'Συμβουλευτικές Υπηρεσίες',
    };
    return options[value as keyof typeof options] || value;
  };

  const getSizeLabel = (value: string) => {
    const options = {
      startup: 'Startup (1-10 άτομα)',
      small: 'Μικρή (11-50 άτομα)',
      medium: 'Μεσαία (51-200 άτομα)',
      large: 'Μεγάλη (201-1000 άτομα)',
      enterprise: 'Επιχείρηση (1000+ άτομα)',
    };
    return options[value as keyof typeof options] || value;
  };

  const getTeamSizeLabel = (value: string) => {
    const options = {
      '1-5': '1-5 μέλη',
      '6-15': '6-15 μέλη',
      '16-30': '16-30 μέλη',
      '31-50': '31-50 μέλη',
      '50+': '50+ μέλη',
    };
    return options[value as keyof typeof options] || value;
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-blue-50 to-orange-50 p-6">
      {/* Decorative Elements */}
      {/* Avatar Circles - Top Left */}
      <div className="absolute top-8 left-8 flex items-center gap-2">
        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-yellow-500 shadow-lg"></div>
        <div className="-ml-3 h-8 w-8 rounded-full bg-gradient-to-br from-orange-400 to-blue-500 shadow-lg"></div>
        <div className="-ml-2 h-6 w-6 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 shadow-lg"></div>
      </div>

      {/* Decorative Circles */}
      <div className="absolute top-20 right-16 h-20 w-20 rounded-full bg-gradient-to-br from-blue-200/30 to-yellow-200/30 blur-sm"></div>
      <div className="absolute bottom-32 left-16 h-16 w-16 rounded-full bg-gradient-to-br from-orange-200/40 to-blue-200/40 blur-sm"></div>
      <div className="absolute right-32 bottom-16 h-12 w-12 rounded-full bg-gradient-to-br from-yellow-200/50 to-orange-200/50 blur-sm"></div>
      <div className="absolute top-1/3 right-8 h-8 w-8 rounded-full bg-gradient-to-br from-blue-200/60 to-yellow-200/60 blur-sm"></div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 w-full max-w-md"
      >
        {/* Header */}
        <div className="mb-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="mb-4 text-4xl font-bold text-gray-900"
          >
            Επιβεβαίωση{' '}
            <span className="relative">
              στοιχείων
              <div className="absolute -bottom-1 left-0 h-3 w-full rounded-full bg-gradient-to-r from-blue-200 via-yellow-200 to-orange-200 opacity-60"></div>
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-gray-600"
          >
            Ελέγξτε τα στοιχεία σας πριν
            <br />
            την τελική δημιουργία
          </motion.p>
        </div>

        {/* Scrollable Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="max-h-96 overflow-y-auto rounded-2xl border border-gray-100 bg-white p-6 shadow-sm"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="mb-1 text-xs font-medium tracking-wide text-gray-400 uppercase">Όνομα Οργανισμού</p>
                <p className="font-medium text-gray-900">{organizationData.name}</p>
              </div>
              <div>
                <p className="mb-1 text-xs font-medium tracking-wide text-gray-400 uppercase">Κλάδος</p>
                <p className="font-medium text-gray-900">{getIndustryLabel(organizationData.industry)}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="mb-1 text-xs font-medium tracking-wide text-gray-400 uppercase">Μέγεθος</p>
                <p className="font-medium text-gray-900">{getSizeLabel(organizationData.size)}</p>
              </div>
              <div>
                <p className="mb-1 text-xs font-medium tracking-wide text-gray-400 uppercase">Τοποθεσία</p>
                <p className="font-medium text-gray-900">{organizationData.location}</p>
              </div>
            </div>

            <div>
              <p className="mb-1 text-xs font-medium tracking-wide text-gray-400 uppercase">Στόχοι Ενέργειας</p>
              <p className="text-sm leading-relaxed text-gray-900">{organizationData.energyGoals}</p>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="mb-1 text-xs font-medium tracking-wide text-gray-400 uppercase">Μέγεθος Ομάδας</p>
                <p className="font-medium text-gray-900">{getTeamSizeLabel(organizationData.teamSize)}</p>
              </div>
              <div>
                <p className="mb-1 text-xs font-medium tracking-wide text-gray-400 uppercase">Email</p>
                <p className="font-medium text-gray-900">{organizationData.contactEmail}</p>
              </div>
            </div>

            {organizationData.contactPhone && (
              <div>
                <p className="mb-1 text-xs font-medium tracking-wide text-gray-400 uppercase">Τηλέφωνο</p>
                <p className="font-medium text-gray-900">{organizationData.contactPhone}</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Success Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4"
        >
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100">
                <Check className="h-4 w-4 text-emerald-600" />
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-emerald-900">Έτοιμοι για δημιουργία!</p>
              <p className="mt-1 text-sm text-emerald-700">
                {isCreating 
                  ? 'Δημιουργώντας τον οργανισμό σας...' 
                  : 'Ο οργανισμός σας θα δημιουργηθεί με τα παραπάνω στοιχεία.'
                }
              </p>
            </div>
          </div>
        </motion.div>

        {/* Terms & Conditions */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.6 }}
          onSubmit={handleSubmit}
          className="mt-6 space-y-4"
        >
          <div className="flex items-center gap-3">
            <div
              onClick={() => !isCreating && setAgreedToTerms(!agreedToTerms)}
              className={`flex h-5 w-5 cursor-pointer items-center justify-center rounded border-2 transition-all ${
                agreedToTerms 
                  ? 'border-black bg-black' 
                  : 'border-gray-300 bg-white hover:border-gray-400'
              } ${isCreating ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {agreedToTerms && (
                <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
            <span className={`text-sm text-gray-600 ${isCreating ? 'opacity-50' : ''}`}>
              Συμφωνώ με τους{' '}
              <button 
                type="button" 
                disabled={isCreating}
                className="font-medium text-black underline hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Όρους & Προϋποθέσεις
              </button>
            </span>
          </div>

          {/* Create Organization Button */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.6 }}
            type="submit"
            disabled={!agreedToTerms || isCreating}
            className={`flex w-full items-center justify-center gap-3 rounded-full py-4 font-medium transition-all ${
              agreedToTerms && !isCreating
                ? 'bg-black text-white hover:bg-gray-800' 
                : 'cursor-not-allowed bg-gray-200 text-gray-400'
            }`}
          >
            {isCreating ? (
              <>
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
                Δημιουργία σε εξέλιξη...
              </>
            ) : (
              <>
                <ArrowRight className="h-5 w-5" />
                Δημιουργία Οργανισμού
              </>
            )}
          </motion.button>
        </motion.form>

        {/* Footer */}
        {!isCreating && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.6 }}
            className="mt-8 text-center"
          >
            <span className="text-gray-500">Θέλετε να επιστρέψετε; </span>
            <button
              onClick={() => navigate('/organizations')}
              className="font-medium text-black underline hover:text-gray-700"
            >
              Επιστροφή
            </button>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default CreateOrganizationStepThreeForm;